import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UiModule } from '@components/ui/ui-module';
import { CardBinder, TopK } from '@models/binder.model';
import { Card, CardSet } from '@models/card.model';
import { Card as CardService } from '@services/card.service';
import { BinderService } from '@services/binder.service';
import { forkJoin, switchMap } from 'rxjs';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Menu } from '@services/menu.service';
import { FormsModule } from '@angular/forms';
import { Global } from '@services/global.service';
import { ScannerService } from '@services/scanner.service';
import { UpdateCard } from '@components/partials/card/update-card/update-card';
import { toObservable } from '@angular/core/rxjs-interop';
import { IdentifyCard } from '@components/partials/card/identify-card/identify-card';
import { NotificationService } from '@services/notification.service';

@Component({
  selector: 'lgb-card-binder-form',
  imports: [
    UiModule,
    DecimalPipe,
    FormsModule,
    CommonModule,
    UpdateCard,
    IdentifyCard
  ],
  templateUrl: './card-binder-form.html',
  styleUrl: './card-binder-form.css',
})
export class CardBinderForm implements OnDestroy{
  //service
  readonly route=inject(ActivatedRoute);
  readonly binderS=inject(BinderService);
  readonly cardS=inject(CardService);
  readonly menu=inject(Menu);
  readonly global=inject(Global);
  readonly scanS=inject(ScannerService);

  //data
  readonly cardBinder=signal<CardBinder>(null);
  readonly cardIndex=signal<Card>(null);
  readonly topK=signal<TopK[]>([]);
  readonly cardSets=signal<CardSet[]>([]);

  //ui
  readonly loading=signal(false);
  readonly name=computed(() => this.cardS.getNameFeat(this.topSelected().card, this.cardBinder().lang));
  readonly cardSet=computed(() => this.cardSets().find(s => s.ident==this.topSelected().card.cardSet));
  readonly setName=computed(() => this.cardS.getCardSetName(this.cardSet(), this.cardBinder().lang));
  readonly action=signal<'form' | 'match'>('form');
  readonly topSelected=signal<TopK>(null);
  readonly topSelected$=toObservable(this.topSelected).subscribe(top => ((top && this.cardBinder())? this.cardBinder().card=top.card.ident : null));
  readonly error=signal<string>(null);
  readonly msg=signal<string>(null);


  constructor(){
    this.loading.set(true);
    this.route.paramMap
      .pipe(
        switchMap((params: any) => forkJoin({
            details: this.binderS.getCard(params.get('ident'), true),
            sets: this.cardS.getCardSets(),
            global: this.global.init()
        }))
      )
      .subscribe(response => {
        this.topK.set(response.details.topk.sort((a: TopK, b: TopK) => b.score-a.score));
        this.cardIndex.set(response.details.cardIndex);
        this.cardSets.set(response.sets);

        this.topSelected.set(this.topK().find(t => t.card.ident==response.details.cardBinder.card));
        if(!this.topSelected()){
          this.topSelected.set({
            card: this.cardIndex(),
            score: 0,
            isBest: false,
            isBestFinal: false,
            rank: 0
          });
        }
        this.cardBinder.set(response.details.cardBinder);

        //modification du bouton du milieu
        this.menu.setAction('card', this.cardBinder().ident);

        this.loading.set(false);
      });
  }

  ngOnDestroy(){
    this.menu.setAction('binder');
  }

  updateCard(){
    this.loading.set(true);
    this.binderS.updateCard(this.cardBinder()).subscribe({
      next: response => {
        this.error.set(null);
        this.msg.set('Carte modifiée.');
        this.loading.set(false);
      },
      error: e => {
        this.error.set(e);
        this.loading.set(false);
      }
    });
  }
}
